import { useState, useEffect } from "react";
import queryString from "query-string";
import Layout from "../components/Layout";
import { Formik, Form } from "formik";
import { validate, defaultSchema } from "../functions/validation";
import Disclaimer from "../components/Disclaimer";
import TextField from "../audi-ui-components/TextField";
import Checkbox from "../audi-ui-components/Checkbox";
import Select from "../audi-ui-components/Select";
import ReturnToButton from "../components/ReturnToButton";
import DealerByPostcodeModule from "../components/DealerByPostcodeModule";
import ModelModule from "../components/ModelModule";
import Spinner from "../audi-ui-components/Spinner";
import Button from "../audi-ui-components/Button";
import API from "../functions/api";
import { formatLibphonenumber } from "../functions/textFormatting";
import { gtmPageview, gtmPush } from "../functions/gtm";
import _find from "lodash/find";
import {
  PATH_F1_ENQUIRY,
  PATH_THANKS,
  LABEL_OPTIN,
  UI_NONE,
  DEFAULT_THEME,
  COUNTRIES,
  COUNTRIES_TOP
} from '../constants';

const F1Enquiry = ({ match, location, history }) => {

  const query = queryString.parse(location.search);
  const theme = query.theme || DEFAULT_THEME;
  const [submitted, setSubmitted] = useState(false);
  const [cta, setCta] = useState(null);
  const [modelUrl, setModelUrl] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);

  const initVals = {
    firstName: "",
    lastName: "",
    emailAddress: "",
    mobilePhone: "",
    optIn: false,
    optOut: false,
    dealerContact: false,
    country: "",
  };

  const validateForm = (values) => {
    var schema = {
      firstName: defaultSchema.firstName,
      lastName: defaultSchema.lastName,
      emailAddress: defaultSchema.emailAddress,
      mobilePhone: {
        presence: {
            message: "Phone number is required",
            allowEmpty: false
        },
        isMobileNumber: {message: "Please enter a valid phone number"}
      },
      country: { presence: { message: "Please make a selection", allowEmpty: false } },
      optIn: { truthy: {message: "This field is required"} },
    };
    if (values.country === "AU") {
      schema.searchPostcode = defaultSchema.postCode;
      schema.dealerName = { presence: { message: "Please select a dealer", allowEmpty: false } };
    }
    var v = validate(values, schema, {format: "firstError", fullMessages: false});
    return v;
  }

  const submitForm = (values, formikProps) => {
    let payload = {...values};
    payload.offerName = match.params.offerName;
    payload.modelVariantName = selectedVariant?.label;
    payload.postCode = payload.searchPostcode;
    if (payload.country !== "AU") {
      payload.dealerId = 1000;
      payload.dealerName = "Audi Test Dealer";
    }
    if (payload.dealer) { payload.dealer = ""; }
    if (payload.country) {
      let countryOpt = _find(COUNTRIES, {value: payload.country});
      payload.countryName = countryOpt?.label;
    }
    if (process.env.RAZZLE_APP_ENV !== "local" || payload.submitToBackend) {
      gtmPush("form", "formSubmit", "f1OfferEnquiry", payload);
      return API({
        method: "POST",
        url: `${process.env.RAZZLE_API_HOST}/1/form`,
        data: {formName: 'dealerOfferEnquiry', payload, variation: 1}
      }).then((result) => {
        gtmPush("form", "formSubmitSuccess", "f1OfferEnquiry", payload);
        setSubmitted(true);
        formikProps.setSubmitting(false);
        postSubmit();
      }).catch((error) => {
        // console.error(error);
        formikProps.setSubmitting(false);
        gtmPush("form", "formSubmitError", "f1OfferEnquiry", payload);
        let msg = "An error has occured";
        let errors = {};
        if (error.response?.data) {
          if (error.response.data.modelState) {
            for (let key in error.response.data.modelState) {
              if (!Object.hasOwnProperty(key)) {
                errors[key] = error.response.data.modelState[key][0];
              }
            }
            msg = "There was a problem with the information you submitted, check the fields above for errors.";
          } else {
            msg = error.response.data.message || "An error has occured";
          }
        }
        errors.submit = msg;
        formikProps.setErrors(errors);
        scrollToError();
      });
    } else {
      // fake submit for frontend testing
      console.log(payload);
      setSubmitted(true);
      formikProps.setSubmitting(false);
      postSubmit();
    }
  }

  const postSubmit = () => {
    let mUrl = "";
    let cta = null;
    if (selectedVariant) {
      let cUrl = selectedVariant?.configuratorUrl;
      let cIdx = cUrl ? cUrl.indexOf("configurator") : null;
      mUrl = cUrl ? cIdx > 0 ? cUrl.substring(0,cIdx) : cUrl : "";
      if (selectedVariant?.label?.toLowerCase() === "rs q8 performance") {
        cta = {url: "https://audifoundation.com.au/prizedraw", text: "Audi Foundation prize draw"};
      }
    }
    setModelUrl(mUrl);
    setCta(cta);
    let newPath = `${PATH_F1_ENQUIRY}/${match.params?.offerName}${PATH_THANKS}`;
    history.push(newPath + location.search);
    gtmPageview(newPath);
    setTimeout(()=>{window.scrollTo(0,0)}, 500);
  }

  const scrollToError = () => {
    setTimeout(() => {
      let el = document.querySelector(".is-invalid");
      if (el) {
        el.scrollIntoView({behavior: "smooth", block: "center"});
      }
    }, 1000);
  };

  useEffect(() => {
    if (match.path === `${PATH_F1_ENQUIRY}/:offerName${PATH_THANKS}`) {
      history.push(`${PATH_F1_ENQUIRY}/${match.params.offerName}${location.search}`);
    }
    gtmPageview(match.url);
  }, []);

  return (
    <Layout ui={UI_NONE} theme={theme}>
      <main className="audi-forms__page">

        {submitted && <div className="pt-3 pt-medium-5">
          <h1 className="aui-headline-3 mb-4">Thank you.</h1>
          <p className="mb-3">Your request has been submitted.</p>
          {modelUrl && <p className="mb-5"><a href={modelUrl} className="aui-textlink">View model page</a></p>}
          {!cta?.url && <ReturnToButton query={query} theme={theme} />}
          {cta?.url && <Button variant="primary" href={cta.url}>{cta.text}</Button>}
        </div>}

        {!submitted && <div className="pt-3 pt-medium-5">
          <h1 className="aui-headline-3 mb-4">Enquire now</h1>
          <p className="mb-3">
            Connect directly with your preferred Audi dealership.
          </p>
          <Formik validate={validateForm} onSubmit={submitForm} initialValues={initVals}>
            {({values, errors, touched, setFieldValue, setFieldTouched, isSubmitting, submitCount}) => (
              <Form>
                {isSubmitting && <div className="spinner-wrapper sticky"><Spinner isContinuous /></div>}
                <ModelModule
                  values={values}
                  errors={errors}
                  touched={touched}
                  submitCount={submitCount}
                  setFieldValue={(fieldName, fieldVal, opt) => {
                    setFieldValue(fieldName, fieldVal);
                    if (fieldName === "variantId") { setSelectedVariant(opt); }
                  }}
                  setFieldTouched={setFieldTouched}
                  includeOptionObjOnChange
                  additionalVariantFields="configuratorUrl"
                  form="startingPrice"
                  theme={theme}
                />
                <div className="row">
                  <div className="col-12 col-small-6">
                    <TextField name="firstName"
                      label="First name*"
                      value={values.firstName}
                      onChange={(v) => { setFieldValue('firstName', v); }}
                      isInvalid={errors.firstName && (touched.firstName || submitCount > 0)}
                      invalidMessage={errors.firstName}
                      onBlur={() => { setFieldTouched('firstName'); }}
                      className="form-field"
                      theme={theme === "dark" ? "light" : null}
                    />
                  </div>
                  <div className="col-12 col-small-6">
                    <TextField name="lastName"
                      label="Last name*"
                      value={values.lastName}
                      onChange={(v) => { setFieldValue('lastName', v); }}
                      isInvalid={errors.lastName && (touched.lastName || submitCount > 0)}
                      invalidMessage={errors.lastName}
                      onBlur={() => { setFieldTouched('lastName'); }}
                      className="form-field"
                      theme={theme === "dark" ? "light" : null}
                    />
                  </div>
                </div>
                <TextField name="emailAddress"
                  label="Email address*"
                  value={values.emailAddress}
                  onChange={(v) => { setFieldValue('emailAddress', v.trim()); }}
                  isInvalid={errors.emailAddress && (touched.emailAddress || submitCount > 0)}
                  invalidMessage={errors.emailAddress}
                  onBlur={() => { setFieldTouched('emailAddress'); }}
                  className="form-field"
                  theme={theme === "dark" ? "light" : null}
                />
                <Select name="country"
                  label="Country*"
                  value={values.country || ""}
                  options={[...COUNTRIES_TOP, {label: "──────────", value: "_", disabled: true}, ...COUNTRIES.sort((a,b) => (a.label > b.label) ? 1 : ((b.label > a.label) ? -1 : 0))]}
                  onChange={(v) => {
                    setFieldValue("country", v);
                    if (v === "AU") {
                      setFieldValue("searchPostcode", "", false);
                    } else {
                      setFieldValue("searchPostcode", "9998", false);
                    }
                    if (values.mobilePhone) {
                      let p = formatLibphonenumber(values.mobilePhone, v);
                      setFieldValue('mobilePhone', p, false);
                    }
                  }}
                  isInvalid={errors.country && (touched.country || submitCount > 0)}
                  onBlur={() => { setFieldTouched('country'); }}
                  className="form-field"
                  theme={theme === "dark" ? "light" : null}
                />
                <TextField name="mobilePhone"
                  label="Mobile number*"
                  value={values.mobilePhone}
                  onChange={(v) => { v = formatLibphonenumber(v, values.country); setFieldValue('mobilePhone', v); }}
                  isInvalid={errors.mobilePhone && (touched.mobilePhone || submitCount > 0)}
                  invalidMessage={errors.mobilePhone}
                  onBlur={() => { setFieldTouched('mobilePhone'); }}
                  className="form-field"
                  theme={theme === "dark" ? "light" : null}
                />
                {values.country === "AU" && <DealerByPostcodeModule
                  values={values}
                  errors={errors}
                  touched={touched}
                  submitCount={submitCount}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  theme={theme}
                />}
                <div className="pt-4">
                  <Checkbox name="optIn"
                    label={<>{LABEL_OPTIN}*</>}
                    checked={!!values.optIn}
                    value=""
                    onChange={(v,e) => { setFieldValue('optIn', e.target.checked); }}
                    onBlur={() => { setFieldTouched('optIn'); }}
                    isInvalid={errors.optIn && (touched.optIn || submitCount > 0)}
                    invalidMessage={errors.optIn}
                    className="form-field"
                    theme={theme === "dark" ? "light" : null}
                  />
                </div>
                {process.env.RAZZLE_APP_ENV === 'local' && <div className="form-field dev-only">
                  <Checkbox
                    name="submitToBackend"
                    label="Submit to backend"
                    checked={!!values.submitToBackend}
                    value=""
                    onChange={(v,e) => { setFieldValue('submitToBackend', e.target.checked); }}
                  />
                </div>}
                <div className="form-field submit">
                  {errors.submit && <p className="aui-color-text-red submit-error mb-2">{errors.submit}</p>}
                  <Button type="submit" variant="primary" theme={theme === "dark" ? "light" : null} isStretched onClick={scrollToError}>
                    Submit
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
          <Disclaimer>
            <p>
              Audi, a division of Volkswagen Group Australia Pty Ltd, will collect and use your personal information for the following purposes: customer and prospective &mdash; customer care, advertising and marketing (including personalisation and segmentation), lead reporting and management, and the provision, administration and improvement of our products, services and customer engagement programs. Where relevant, we may also process your information to respond to enquiries and complaints, manage events and test &mdash; drive requests, and undertake analytics and reporting to improve customer experience.
            </p>
            <p>
              We may disclose your information to Audi AG, other Audi importers (including the Audi importer in the country you selected above), authorised dealers, and trusted service providers who support the operation of our business and services.
            </p>
            <p>
              We may also transfer your information to recipients located outside Australia, including jurisdictions that may not offer the same level of data protection as Australia or the EU/EEA. Where your information is transferred internationally, we implement appropriate safeguards and take reasonable steps to ensure overseas recipients handle your personal information in accordance with applicable privacy requirements and local regulations.
            </p>
            <p>
              We process your information in accordance with the Privacy Act 1988 (Cth) and, where applicable, the GDPR and any other relevant local privacy laws.
            </p>
            <p>
              We only share your information with third parties where this is necessary to provide our services, fulfil your request, or as required by law or enforceable regulatory or judicial order.
            </p>
            <p>
              If you consent, Audi AG, other Audi importers (including the Audi importer in the country you selected above), authorised dealers, and trusted service providers may also use your personal information to send you communications concerning products, offers, news and events. Please note that Audi and Audi Dealers are separate organisations, and that Dealers will handle your information in accordance with their own privacy policies.
            </p>
            <p>
              For more details about how we handle your personal information, your rights, and how to contact us, please refer to our <a href="https://www.audi.com.au/en/privacy-policy/" className="aui-textlink" target="_blank" rel="noopener noreferrer">Privacy Policy</a> or contact our Privacy Officer at: <a href="mailto:privacy@volkswagen.com.au" className="aui-textlink">privacy@volkswagen.com.au</a>.
            </p>
          </Disclaimer>
        </div>}

      </main>
    </Layout>
  );
}

export default F1Enquiry;
